import router from '@/router'
import settings from '@/settings'
import { User } from '@/logic/users/user/user'
import { ref } from 'vue'

const isMobileRef = ref(typeof window !== 'undefined' ? window.innerWidth < 480 : false)
const forcedView = ref(localStorage.getItem('forcedView'))

if (typeof window !== 'undefined') {
  window.addEventListener('resize', () => {
    const nowIsMobile = window.innerWidth < 480
    if (isMobileRef.value !== nowIsMobile) {
      isMobileRef.value = nowIsMobile
    }
  })
}

//todo: the isMobile and isDesktop are true and false because the desktop view is in development phase.

const filters = {
  get isMobile() {
    const isSpecial = !this.routeIsAdmin() && this.admin() && forcedView.value === 'desktop'
    return isSpecial ? isMobileRef.value : true
  },
  get isDesktop() {
    const isSpecial = !this.routeIsAdmin() && this.admin() && forcedView.value === 'desktop'
    return isSpecial ? !isMobileRef.value : false
  },
  isDesktopForRoute(route) {
    const routeIsAdmin = route?.matched?.some((match) => match.name == 'admin')
    return !routeIsAdmin && this.admin() && forcedView.value === 'desktop' && !isMobileRef.value
  },
  admin() {
    return User.ME?.isAdmin
  },
  hasAdminRole(...roles) {
    const roleNames = User.ME?.roleNames ?? []
    if (!roleNames.length) return false
    if (roleNames.includes('SUPER_ADMIN')) return true
    return roles.some((role) => roleNames.includes(role))
  },
  toggleView() {
    if (forcedView.value === 'desktop') {
      forcedView.value = 'mobile'
    } else {
      forcedView.value = 'desktop'
    }
    localStorage.setItem('forcedView', forcedView.value)
  },
  persianToEnglishDigits(txt) {
    if (!(typeof txt == 'string')) return txt
    let persian_digits_map = {
      '۱': '1',
      '۲': '2',
      '۳': '3',
      '۴': '4',
      '۵': '5',
      '۶': '6',
      '۷': '7',
      '۸': '8',
      '۹': '9',
      '۰': '0'
    }

    let new_txt = ''

    for (let c of txt) {
      if (c in persian_digits_map) new_txt = new_txt + persian_digits_map[c]
      else new_txt = new_txt + c
    }

    return new_txt
  },
  EnglishToPersianDigits(txt) {
    if (!(typeof txt == 'string')) txt = txt.toString()
    let persian_digits_map = {
      1: '۱',
      2: '۲',
      3: '۳',
      4: '۴',
      5: '۵',
      6: '۶',
      7: '۷',
      8: '۸',
      9: '۹',
      0: '۰'
    }
    let new_txt = ''
    for (let c of txt) {
      if (c in persian_digits_map) new_txt = new_txt + persian_digits_map[c]
      else new_txt = new_txt + c
    }
    return new_txt
  },
  formatPhoneNumber(phoneNumber) {
    if (phoneNumber == null) return null

    return phoneNumber.replace('+98', '0')
  },
  cleanPhoneNumber(phoneNumber) {
    if (phoneNumber == null) return phoneNumber

    return phoneNumber.replace(/\s+/g, '').replace(/[()]/g, '')
  },
  isIranPhoneNumber(phoneNumber) {
    if (phoneNumber == null) return false

    return (
      phoneNumber.startsWith('+98') ||
      phoneNumber.startsWith('(+98') ||
      phoneNumber.startsWith('( +98')
    )
  },
  convertNumberToMillions(number) {
    let num = parseFloat(number)
    if (isNaN(num)) return number
    const million = 1000000
    let formattedNumber = Math.floor(num / million)
    return this.prettyNumber(formattedNumber.toString())
  },

  prettyPositiveInteger(number) {
    number = number.toString()
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  },
  prettyInteger(number) {
    number = number.toString()
    if (number.includes('-')) {
      number = number.replace('-', '')
      return '-' + filters.prettyPositiveInteger(number)
    }
    return filters.prettyPositiveInteger(number)
  },
  prettyNumber(number) {
    number = number.toString()
    if (number.includes('.')) {
      var parts = number.toString().split('.')
      parts[0] = filters.prettyInteger(parts[0])
      return parts[1] === '' ? parts[0] : parts.join('.')
    }
    return filters.prettyInteger(number)
  },
  formatDecimal(decimal, digitAfterDecimal = 8, floor = false) {
    if (!decimal && decimal !== 0) return ''
    if (decimal === null || decimal === '') return ''
    if (floor) {
      const factor = Math.pow(10, digitAfterDecimal)
      return Math.floor(parseFloat(decimal) * factor) / factor
    }
    return Number(parseFloat(decimal).toFixed(digitAfterDecimal))
  },
  prettyAndFormatDecimal(decimal, digitAfterDecimal = 1, floor = false) {
    return this.prettyNumber(this.formatDecimal(decimal, digitAfterDecimal, floor))
  },
  formatNumberString(numberStr, allowedDecimals = 20) {
    if (!numberStr.includes('.')) return numberStr

    let decimalPlaces = numberStr.length - numberStr.indexOf('.') - 1
    if (decimalPlaces > allowedDecimals)
      return numberStr.slice(0, -(decimalPlaces - allowedDecimals))
    return numberStr
  },
  numberToString(number) {
    let newValue = number.toString()
    if (newValue.includes('e')) {
      let pw = -Number(newValue.split('e')[1])
      let meanPart = newValue.split('e')[0]
      newValue = this.move(meanPart, pw)
    }
    return newValue
  },
  truncateDecimal(decimalStr, decimalPlaces) {
    if (!decimalStr.includes('.')) return decimalStr

    return decimalStr.slice(0, decimalStr.indexOf('.') + decimalPlaces + 1)
  },
  truncateIntegerPart(decimalStr, integerPartLength) {
    if (decimalStr.includes('.'))
      return (
        this.truncateIntegerPart(decimalStr.slice(0, decimalStr.indexOf('.')), integerPartLength) +
        decimalStr.slice(decimalStr.indexOf('.'))
      )

    if (decimalStr.length <= integerPartLength) return decimalStr
    return decimalStr.slice(decimalStr.length - integerPartLength)
  },
  decimalPartLength(decimalStr) {
    if (!decimalStr.includes('.')) return 0

    return decimalStr.slice(decimalStr.indexOf('.') + 1).length
  },
  integerPartLength(decimalStr) {
    if (decimalStr.includes('.'))
      return this.integerPartLength(decimalStr.slice(0, decimalStr.indexOf('.')))

    return decimalStr.length
  },
  move(number, position) {
    let res = ''
    if (position > 0) {
      res = this.moveRight(number, position)
    } else res = this.moveLeft(number, -position)

    while (res.includes('.') && (res.slice(-1) == '0' || res.slice(-1) == '.'))
      res = res.slice(0, -1)

    return res
  },
  moveRight(number, position) {
    if (!number.includes('.')) number = number + '.0'

    while (position) {
      let pointPosition = number.indexOf('.')
      let next = number[pointPosition - 1]
      number = this.replaceAt(number, pointPosition, next)
      number = this.replaceAt(number, pointPosition - 1, '.')
      if (number[0] == '.') number = '0' + number
      position -= 1
    }

    return number
  },
  moveLeft(number, position) {
    if (!number.includes('.')) number = number + '.0'

    while (position) {
      let pointPosition = number.indexOf('.')
      let next = number[pointPosition + 1]
      number = this.replaceAt(number, pointPosition, next)
      number = this.replaceAt(number, pointPosition + 1, '.')
      if (number.slice(-1) == '.') number = number + '0'
      position -= 1
    }
    return number
  },
  replaceAt(str, index, replacement) {
    return str.substring(0, index) + replacement + str.substring(index + replacement.length)
  },
  removeDecimalPart(decimal) {
    return Math.trunc(decimal)
  },
  mask(text, maxLength = 7, maxStarCount = 5) {
    if (text === undefined || text === null) return text
    let result = ''
    let starCount = 0
    for (let i = 0; i < text.length; i++) {
      if (i < maxLength) result += text[i]
      else if (i >= text.length - maxLength) result += text[i]
      else if (starCount < maxStarCount) {
        result += '*'
        starCount += 1
      }
    }
    return result
  },
  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  },
  camelize(str) {
    let camelCase = str
      .toLowerCase()
      .replace(/--([a-z])/g, (_, letter) => ` ${letter.toUpperCase()}`)
      .replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''))
    return this.capitalize(camelCase)
  },
  camelToSnakeCase(str) {
    var result = str.replace(/([A-Z])/g, ' $1')
    result = result.slice(1)
    return result.split(' ').join('-').toLowerCase()
  },
  splitCamelCase(str) {
    return str.split(/(?=[A-Z])/).join(' ')
  },
  generateUUID() {
    var d = new Date().getTime()
    var d2 =
      (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16
      if (d > 0) {
        r = (d + r) % 16 | 0
        d = Math.floor(d / 16)
      } else {
        r = (d2 + r) % 16 | 0
        d2 = Math.floor(d2 / 16)
      }
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
    })
  },
  removeNullAndEmptyValues(obj) {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value === null) return acc
      if (value === '') return acc
      if (
        value &&
        value.constructor === Object &&
        Object.keys(this.removeNullAndEmptyValues(value)).length === 0
      )
        return acc
      acc[key] = value
      return acc
    }, {})
  },
  uniqueByField(array, fieldLambda) {
    const seen = new Set()
    return array.filter((item) => {
      if (item == null) return true
      const value = fieldLambda(item)
      if (seen.has(value)) {
        return false
      }
      seen.add(value)
      return true
    })
  },
  copyToClipboard(text) {
    const textarea = document.createElement('textarea')
    textarea.value = text

    textarea.style.position = 'fixed'
    textarea.style.top = '-9999px'

    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
  },
  datetimeToString(datetime) {
    let startDate = datetime.toISOString()
    startDate = startDate.replace(' ', 'T')
    return startDate
  },
  routeIsAdmin() {
    return router.currentRoute.value.matched.find((match) => match.name == 'admin') != undefined
  },
  productIsMain() {
    let product = settings.PRODUCT
    return ['prime', 'cms'].includes(product)
  },
  getTimeDiff(date1, date2) {
    return Math.abs(Math.floor((date2.getTime() - date1.getTime()) / 1000))
  },
  getBiggestTimeDiff(date1, date2) {
    const diffInSeconds = this.getTimeDiff(date1, date2)
    const units = [
      { label: 'year', seconds: 365 * 24 * 60 * 60 },
      { label: 'month', seconds: 30 * 24 * 60 * 60 },
      { label: 'week', seconds: 7 * 24 * 60 * 60 },
      { label: 'day', seconds: 24 * 60 * 60 },
      { label: 'hour', seconds: 60 * 60 },
      { label: 'minute', seconds: 60 },
      { label: 'second', seconds: 1 }
    ]

    for (let unit of units) {
      const amount = Math.floor(diffInSeconds / unit.seconds)
      if (amount >= 1) {
        return `${amount} ${unit.label}${amount > 1 ? 's' : ''}`
      }
    }
    return '0 seconds'
  },
  deepEqual(obj1, obj2) {
    if (obj1 === obj2) return true
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) {
      return false
    }

    const keys1 = Object.keys(obj1)
    const keys2 = Object.keys(obj2)
    if (keys1.length !== keys2.length) {
      return false
    }

    for (let key of keys1) {
      if (!keys2.includes(key) || !this.deepEqual(obj1[key], obj2[key])) {
        return false
      }
    }
    return true
  },
  isEmpty(value) {
    return (
      value === '' ||
      value == null ||
      (Array.isArray(value) && value.length === 0) ||
      (value instanceof Map && value.size === 0) ||
      (typeof value === 'object' &&
        !(value instanceof Map) &&
        Object.keys(value || {}).length === 0)
    )
  },
  fixLanDir(text) {
    if (text === null) {
      return ''
    }
    // todo: fix it so it works with html
    return text
    const hasRTL = (x) => /[\u0600-\u06FF]/.test(x)
    const hasLTR = (x) => /[a-zA-Z0-9]/.test(x)
    if (!hasLTR(text) || !hasRTL(text)) {
      return text
    }

    const messageParts = String(text).split(' ')
    const messageResult = []
    for (let i = 0; i < messageParts.length; i++) {
      const messagePart = messageParts[i]

      if (hasRTL(messagePart)) {
        messageResult.push('\u2067' + messagePart + '\u2069')
      } else {
        messageResult.push('\u2066' + messagePart + '\u2069')
      }
    }
    return messageResult.join(' ')
  }
}

export default filters
export { filters }
